import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { sha384 } from "crypto-hash";
import EverlakeLoadingOverlay from "./General/EverlakeLoadingOverlay";

import config from "config/config";
import { useTrackLoginAttempt, useTrackLogin } from "logging";
import { getEmailHref } from "js/formatting";

import {
  searchPolicies,
  removePolicies,
} from "redux/slices/searchPoliciesSlice";
import { removeUser, setUser } from "redux/slices/userSlice";
import { getAgent } from "redux/agent/slices/getAgentSlice";
import { setCsrUser } from "redux/slices/csrUserSlice";

import { login } from "service/userservice/login";
import { sendEmailNotification } from "service/notificationservice/sendEmailNotification";

import LoginOTPModal from "components/ModalDialogs/LoginOTPModal";
import FormInputError from "components/General/FormInputError";
import DomainErrorModal from "./ModalDialogs/DomainErrorModal";

export default function LoginCard({ loginType, setHideCustomChat }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openEye, setOpenEye] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loginError, setloginError] = useState("");
  const [loginOTP, setloginOTP] = useState("");
  const [modalState, setModalState] = useState(false);
  const [errorInfo, seterrorInfo] = useState("");
  const [errorTitle, setErrorTitle] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [OpenOTPDialog, setOpenOTPDialog] = useState(false);
  const [showLoginError, setShowLoginError] = useState(false);
  const [userResponseObj, setUserResponseObj] = useState({});
  const [locked, setLocked] = useState(false);
  const [showResetPwdLink, setShowResetPwdLink] = useState(false);
  const trackLoginAttempt = useTrackLoginAttempt();
  const trackLogin = useTrackLogin();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const toggle = () => {
    setOpenEye(!openEye);
  };

  async function addCSRUserObjectToRedux(userResponse) {
    return new Promise((res, rej) => {
      res(dispatch(setCsrUser(userResponse)));
    });
  }
  async function addCSRUser(userResponse) {
    await addCSRUserObjectToRedux(userResponse);
    if (loginType === "agent") navigate("/dashboard");
    else navigate("/policies");
  }

  async function addUserObjectToRedux(userResponse) {
    return new Promise((res, rej) => {
      res(dispatch(setUser(userResponse)));
    });
  }
  async function addUserObject(userResponse) {
    await addUserObjectToRedux(userResponse);
  }

  async function afterValidatingOTP(userObj) {
    setHideCustomChat(true);

    let userResponse = {};
    if (OpenOTPDialog) {
      setOpenOTPDialog(false);
      userResponse = userResponseObj;
    } else {
      userResponse = userObj;
    }

    if (userResponse.isVerified === 0) {
      window.localStorage.setItem("firstLoginUserId", userResponse.userId);
      window.localStorage.setItem("firstLoginUserEmail", userResponse.emailId);
      window.localStorage.setItem("firstLoginUserName", userResponse.firstName);
      navigate("/reset-password");
    } else {
      trackLogin(userResponse);

      if (
        userResponse?.role?.roleName?.toLowerCase() === "csr" ||
        userResponse?.role?.roleName?.toLowerCase() === "csr admin"
      ) {
        addCSRUser(userResponse);
      } else {
        await addUserObject(userResponse);
        setLoading(true);
        if (loginType === "agent") {
          // clear oppesite portal polices
          dispatch(removePolicies());

          // this is for profile page setup
          dispatch(
            getAgent({
              agentId: userResponse.agentViewingId,
              updateStore: true,
            })
          );

          // fetch by agent hierarchy policy ID(s)
          setLoading(false);
          setHideCustomChat(false);
          navigate("/dashboard");
        } else {
          // clear oppesite portal polices

          // fetch by user identityID
          dispatch(searchPolicies(userResponse.identityNumber)).then(
            (response) => {
              if (response?.meta?.requestStatus === "fulfilled") {
                if (
                  response?.payload?.hasDomesticViolencePolicy === true ||
                  (response?.payload?.hasOwnerDeathClaimExists === true &&
                    response?.payload?.policies?.length === 0)
                ) {
                  dispatch(removeUser());
                  setLoading(false);
                  setModalState(true);
                  setErrorTitle(
                    t("CustomerLogin-domestic-violence-error-title")
                  );
                  response?.payload?.hasOwnerDeathClaimExists === true
                    ? seterrorInfo(
                        <Trans
                          i18nKey={
                            "CustomerLogin-death-claim-exists-error-message"
                          }
                          components={{
                            link1: (
                              <Link
                                className="text-ui-blue underline"
                                to="/guest/help"
                              ></Link>
                            ),
                          }}
                        />
                      )
                    : seterrorInfo(
                        t("CustomerLogin-domestic-violence-error-message")
                      );
                } else {
                  setLoading(false);
                  setHideCustomChat(false);
                  navigate("/policies");
                }
              } else {
                setLoading(false);
                setloginError(t("Login-server-error"));
                setShowLoginError(true);
                console.error("Something wrong.", response?.payload?.status);
              }
            }
          );
        }
      }
    }
  }

  function randomNumberInRange(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  async function onSubmit(data) {
    setLoading(true);

    trackLoginAttempt(data.username);

    const userResponse = await login(
      data.username,
      (await sha384(data.password)).toString().substring(15, 30),
      loginType
    ).catch((error) => {
      setLoading(false);
      setloginError(t("Login-server-error"));
      setShowLoginError(true);
      console.error("Something wrong.", error);
    });

    if (userResponse.status === undefined) {
      setUserResponseObj(userResponse);
      setUserEmail(userResponse.emailId);
      let passcode = randomNumberInRange(100000, 999999);
      setloginOTP(passcode.toString());
      setLoading(true);
      let securitycode = randomNumberInRange(100000, 999999);
      let sec =
        parseInt(securitycode.toString().substring(3, 4)) === 0
          ? 1
          : parseInt(securitycode.toString().substring(3, 4));
      let confirmationcode = passcode * sec;
      let contentDictionary = {};
      contentDictionary.confirmationcode = confirmationcode.toString();
      contentDictionary.securitycode = securitycode.toString();

      loginType === "agent"
        ? await sendEmailNotification(
            userResponse.emailId,
            12,
            contentDictionary
          )
            .then((response) => {
              setLoading(false);
              setOpenOTPDialog(true);
            })
            .catch((error) => {
              console.error(error);
              setLoading(false);
            })
        : await sendEmailNotification(
            userResponse.emailId,
            2,
            contentDictionary
          )
            .then((response) => {
              setLoading(false);
              setOpenOTPDialog(true);
            })
            .catch((error) => {
              console.error(error);
              setLoading(false);
            });
    } else {
      setLocked(false);
      if (userResponse.status === 403) {
        if (userResponse.title === "User is locked") {
          setLocked(true);
          setloginError(
            t("Login-login-error-1", {
              email: config.CUSTOMER_SERVICE_EMAIL,
            })
          );
          setShowLoginError(true);
        } else if (userResponse.title === "Last login attempt alert") {
          setloginError(t("Login-login-error-2"));
          setShowLoginError(true);
          setShowResetPwdLink(true);
        } else if (userResponse.title === "User password expired") {
          setloginError(t("Login-login-error-3"));
          setShowLoginError(true);
          setShowResetPwdLink(true);
        }
      } else if (userResponse.status === 401) {
        setloginError(t("Login-login-error-4"));
        setShowLoginError(true);
      } else if (userResponse.status === 404) {
        setloginError(t("Login-login-error-5"));
        setShowLoginError(true);
      }
      setLoading(false);
    }
  }

  return (
    <div className="ui-card text-center p-4 relative xl:p-6">
      <h2>{t("Login-title")}</h2>
      <EverlakeLoadingOverlay isLoading={loading}>
        {showLoginError ? (
          <div>
            {locked ? (
              <p className="text-sm ui-input-error text-left mb-6">
                {loginType === "agent" ? (
                  <Trans
                    i18nKey="Login-login-error-7"
                    values={{ email: config.AGENT_SERVICE_EMAIL }}
                    components={{
                      link1: (
                        <a href={getEmailHref(config.AGENT_SERVICE_EMAIL)}>
                          {config.AGENT_SERVICE_EMAIL}
                        </a>
                      ),
                    }}
                  />
                ) : (
                  <Trans
                    i18nKey="Login-login-error-6"
                    values={{ email: config.CUSTOMER_SERVICE_EMAIL }}
                    components={{
                      link1: (
                        <a href={getEmailHref(config.CUSTOMER_SERVICE_EMAIL)}>
                          {config.CUSTOMER_SERVICE_EMAIL}
                        </a>
                      ),
                      link2: <Link to="/help#contact-us"></Link>,
                    }}
                  />
                )}
              </p>
            ) : (
              <p className="text-sm ui-input-error text-left mb-6">
                {loginError}
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm mb-6">{t("Login-subtitle")}</p>
        )}
        <form onSubmit={handleSubmit(onSubmit)} aria-label="login form">
          <div className="text-left max-w-xs mx-auto">
            <div className="mb-4">
              <label
                id="ID_usernameLabel"
                htmlFor="ID_username"
                className="block mb-1"
              >
                {t("Login-label")}
              </label>
              <input
                id="ID_username"
                type="text"
                placeholder={t("Login-placeholder")}
                aria-labelledby="ID_usernameLabel"
                aria-describedby="ID_usernameError"
                {...register("username", {
                  required: {
                    value: true,
                    message: "Please enter username.",
                  },
                })}
              />
              <div id="ID_usernameError">
                {errors.username && (
                  <div className="ui-input-error">
                    {errors.username.message}
                  </div>
                )}
              </div>
            </div>
            <div className="mb-6 relative">
              <label
                id="ID_passwordLabel"
                htmlFor="ID_password"
                className="block mb-1"
              >
                {t("Login-label-2")}
              </label>
              <input
                id="ID_password"
                type={openEye ? "password" : "text"}
                placeholder={t("Login-placeholder-2")}
                aria-labelledby="ID_passwordLabel"
                autoComplete="off"
                {...register("password", {
                  required: {
                    value: true,
                    message: "Please enter password.",
                  },
                })}
              />
              <div className="absolute top-8 right-4">
                {openEye ? (
                  <AiOutlineEye onClick={toggle} />
                ) : (
                  <AiOutlineEyeInvisible onClick={toggle} />
                )}
              </div>
              <FormInputError
                id="ID_passwordError"
                errorMessage={errors?.password?.message}
              />
            </div>
          </div>
          <button className="ui-btn-primary min-w-[144px] mb-6 mx-auto">
            {t("Login-button")}
          </button>
        </form>
        <p className="text-xs mb-3">
          {showResetPwdLink ? (
            <Trans
              i18nKey="Login-link-1"
              components={{
                link1: <Link to="/forgot-password" className="font-semibold" />,
              }}
            />
          ) : (
            <Trans
              i18nKey="Login-link-2"
              components={{
                link1: <Link to="/forgot-password" className="font-semibold" />,
              }}
            />
          )}
        </p>
        {config.COMPANY_NAME === "everlake" && (
          <p className="text-xs">
            <Trans
              i18nKey="Login-link-3"
              components={{
                link1: <Link to="/register" className="font-semibold" />,
              }}
            />
          </p>
        )}
      </EverlakeLoadingOverlay>
      <LoginOTPModal
        dialogState={OpenOTPDialog}
        setDialogState={setOpenOTPDialog}
        loginOTP={loginOTP}
        setloginOTP={setloginOTP}
        onSubmitOTP={afterValidatingOTP}
        userEmail={userEmail}
        loginType={loginType}
      />
      <DomainErrorModal
        modalState={modalState}
        setModalState={setModalState}
        errorInfo={errorInfo}
        title={errorTitle}
      />
    </div>
  );
}
